import { useState } from "react";
import { BreadBudget } from "breadcommon";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "../rye/RyeButton";
import { RyeSegmentedButton } from "../rye/RyeSegmentedButton";
import type { ColoredCategoryType } from "../common/categoryTypeColor";
import { BudgetBars } from "./BudgetBars";
import { BudgetBarRow, BudgetBarScale, useBudgetBars } from "./budgetBarRows";

export type BudgetTypeFilter = ColoredCategoryType | "all";

const TYPE_FILTERS: { id: BudgetTypeFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "expenses", label: "Expenses" },
  { id: "income", label: "Income" },
  { id: "savings", label: "Savings" },
];

/** The graph's own controls, beside the report's view switch. */
export function BudgetTrackingBarsControls({
  type,
  setType,
  scale,
  setScale,
}: {
  type: BudgetTypeFilter;
  setType: (type: BudgetTypeFilter) => void;
  scale: BudgetBarScale;
  setScale: (scale: BudgetBarScale) => void;
}): JSX.Element {
  return (
    <div className={buildClasses("flex", "items-center", "gap-3")}>
      <RyeSegmentedButton
        entries={TYPE_FILTERS.map((filter) => ({
          id: filter.id,
          displayText: filter.label,
          onClick: () => setType(filter.id),
        }))}
        initEntryId={type}
      />
      <RyeSegmentedButton
        entries={[
          {
            id: "dollars",
            displayText: "Raw",
            tooltip: "Bar lengths are not scaled",
            onClick: () => setScale("dollars"),
          },
          {
            id: "budget",
            displayText: "Normalized",
            tooltip: "Scale bars so each budgeted bar is the same length",
            onClick: () => setScale("budget"),
          },
        ]}
        initEntryId={scale}
      />
    </div>
  );
}

/** The budget tracking graph's bars, filtered and scaled by its controls. */
export function BudgetTrackingBars({
  budget,
  selectedId,
  onSelectRow,
  type,
  scale,
}: {
  budget: BreadBudget;
  selectedId: string | null;
  onSelectRow: (row: BudgetBarRow) => void;
  type: BudgetTypeFilter;
  scale: BudgetBarScale;
}): JSX.Element {
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const groups = useBudgetBars(budget, type);
  const expandableGroupIds = groups
    .filter((group) => group.categories.length > 0)
    .map((group) => group.id);
  const allExpanded =
    expandableGroupIds.length > 0 &&
    expandableGroupIds.every((groupId) => expandedGroupIds.has(groupId));

  function toggleGroup(groupId: string) {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  return (
    <BudgetBars
      headerStart={
        expandableGroupIds.length > 0 && (
          <RyeButton
            text={allExpanded ? "Collapse all" : "Expand all"}
            icon={allExpanded ? "unfold_less" : "unfold_more"}
            size="sm"
            variant="transparent"
            vibe="subdued"
            onClick={() =>
              setExpandedGroupIds(
                allExpanded ? new Set() : new Set(expandableGroupIds),
              )
            }
          />
        )
      }
      groups={groups}
      expandedGroupIds={expandedGroupIds}
      onToggleGroup={toggleGroup}
      scale={scale}
      currency={budget.currency}
      selectedId={selectedId}
      onSelectRow={onSelectRow}
    />
  );
}
